/**
 * The team's follow-up on an FDFC Tracker entry: the phone call, and what the
 * person said about the kit. Values, labels and colours are shared by the
 * backend checks, the list dropdowns and the detail dialog.
 */
// ---- Call ----
export const CALL_STATUSES = [
    'not_called',
    'no_call_needed',
    'received',
    'not_received',
    'not_reachable',
    'callback_later',
];
/** In the order the dropdown offers them. */
export const CALL_STATUS_OPTIONS = [
    { value: 'not_called', label: 'Call Pending' },
    { value: 'no_call_needed', label: 'No need to call' },
    { value: 'received', label: 'Call Received' },
    { value: 'not_received', label: 'Call Not Received' },
    { value: 'not_reachable', label: 'Call Not Reachable' },
    { value: 'callback_later', label: 'Call Back later' },
];
export const CALL_STATUS_LABEL = Object.fromEntries(CALL_STATUS_OPTIONS.map((o) => [o.value, o.label]));
export const CALL_STATUS_TONE = {
    not_called: 'neutral',
    no_call_needed: 'ok',
    received: 'ok',
    not_received: 'bad',
    not_reachable: 'warn',
    callback_later: 'warn',
};
// ---- Survey ----
export const SURVEY_OUTCOMES = ['received_good', 'received_damaged', 'not_received'];
export const SURVEY_OUTCOME_OPTIONS = [
    { value: 'received_good', label: 'Received, good' },
    { value: 'received_damaged', label: 'Received, damaged' },
    { value: 'not_received', label: 'Not received' },
];
export const SURVEY_OUTCOME_LABEL = Object.fromEntries(SURVEY_OUTCOME_OPTIONS.map((o) => [o.value, o.label]));
export const SURVEY_OUTCOME_TONE = {
    received_good: 'ok',
    received_damaged: 'bad',
    not_received: 'warn',
};
/** Shown while an entry has no survey answer at all. */
export const NO_SURVEY_LABEL = 'No survey yet';
/** Outcomes that ask "is another box going out, and what goes in it". */
export const SURVEY_OUTCOMES_WITH_CLAIM = ['received_damaged', 'not_received'];
/**
 * Whether a damaged / missing kit is being honoured. Deliberately coarse: the
 * fork that decides whether the row is still work or is finished.
 */
export const DAMAGE_CLAIMS = ['redispatch', 'false_claim'];
export const DAMAGE_CLAIM_OPTIONS = [
    { value: 'redispatch', label: 'Need to dispatch' },
    { value: 'false_claim', label: 'No need to dispatch' },
];
export const DAMAGE_CLAIM_TONE = {
    redispatch: 'warn',
    false_claim: 'neutral',
};
/** The short form for a chip. */
export function claimLabel(claim) {
    if (!claim)
        return null;
    return claim === 'redispatch' ? 'dispatch again' : 'no dispatch';
}
export function claimFullLabel(claim) {
    return claim ? (DAMAGE_CLAIM_OPTIONS.find((o) => o.value === claim)?.label ?? null) : null;
}
/** What goes back in the box. Several can be true at once. */
export const DAMAGE_AREAS = [
    'full_kit',
    'certificate_book',
    'mug',
    'keychain',
    'book',
    'certificate',
    'stand',
    'certificate_stand',
    'keychain_stand',
    'other',
];
/** Names for every item ever offered, so older answers still read correctly. */
const DAMAGE_AREA_LABEL = {
    full_kit: 'Full Kit',
    certificate_book: 'Certificate + Book',
    mug: 'Mug',
    keychain: 'Key-chain',
    book: 'Book',
    certificate: 'Certificate',
    stand: 'Stand',
    certificate_stand: 'Certificate + Stand',
    keychain_stand: 'Key-chain + Stand',
    other: 'Something else…',
};
/** What the dispatch popup offers now: only the certificate is re-sent. */
export const OFFERED_DAMAGE_AREAS = ['certificate'];
export const isOfferedDamageArea = (a) => OFFERED_DAMAGE_AREAS.includes(a);
export const DAMAGE_AREA_OPTIONS = OFFERED_DAMAGE_AREAS.map((value) => ({ value, label: DAMAGE_AREA_LABEL[value] }));
/**
 * The short form for a chip: "Mug + Book". `other` shows what was typed — the
 * only informative part — rather than "Something else".
 */
export function damageLabel(areas, detail) {
    if (!areas || areas.length === 0)
        return null;
    return areas
        .map((a) => (a === 'other' ? detail?.trim() || 'something else' : (DAMAGE_AREA_LABEL[a] ?? a)))
        .join(' + ');
}
//# sourceMappingURL=tracker-fdfc-follow-up.js.map